/**
 * Supplement plans, from the server — a client's, or the whole workspace's —
 * and one plan, whole.
 *
 * The page is the server's: the panel asks for one page's worth and gets
 * exactly that, plus the `count` of everything the filter matches, which is
 * what it divides into pages to draw the control with. Turning a page is
 * therefore another call, and so is switching the Draft tab or typing in the
 * search box — this endpoint takes a `status` and a `search`, unlike the
 * exercise list, so both narrow the whole list rather than the page in hand.
 *
 * Which page is the caller's to hold, not this hook's: it is the thing the
 * panel has to reset when the search changes, and the hook has no way of
 * knowing that a change came from the user rather than from a re-render.
 *
 * Reading only, here. The writes are `useSupplementPlanWrites` at the foot of
 * this file — the panel lists and the create screen writes, and neither wants
 * the other's query running beside it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
// The Create plan screen's totals count this kind too, and a plan written
// here without leaving that screen has to move the figure above it.
import { PLAN_COUNTS_QUERY_KEY } from "../../mealPlans/hooks/usePlanCounts";
import {
  DEFAULT_PAGE_SIZE,
  createSupplementPlan,
  duplicateSupplementPlan,
  fetchSupplementPlan,
  fetchSupplementPlans,
  sendSupplementPlan,
  updateSupplementPlan,
} from "../services/supplementPlansService";
import type {
  PlanSupplement,
  SupplementPlanRecord,
  // Aliased: `SupplementPlanState` is already the name of what
  // `useSupplementPlan` returns, below.
  SupplementPlanState as PlanStatus,
  SupplementPlanSummary,
} from "../types/supplements.types";

/** Everything this module caches, which every write throws away. */
export const SUPPLEMENTS_QUERY_KEY = ["supplements"] as const;

/** How long the box waits after the last keystroke before it asks. */
const SEARCH_DEBOUNCE_MS = 300;

export type SupplementPlansStatus = "loading" | "ready" | "failed";

export interface SupplementPlansState {
  /** One page of them — `pageSize` rows at most, not the whole list. */
  plans: SupplementPlanSummary[];
  /** How many the server holds for this client and search, not how many came back. */
  count: number;
  status: SupplementPlansStatus;
  /** Set only when `status` is `failed` — what the panel shows. */
  error?: string;
  /**
   * Whether the server refused the read outright rather than failing to
   * answer it — a 403, which is what a workspace with no plan running gets.
   * Not an error the reader can do anything about, and not news either: a
   * practice that has never been able to write a supplement plan has never
   * written one. The panel draws it as the empty list it is.
   */
  refused: boolean;
  reload: () => void;
}

export interface UseSupplementPlansOptions {
  /**
   * A client's `public_id` — whose plans are asked for. Absent asks for the
   * whole workspace, which is what the Create plan screen lists.
   */
  client?: string;
  /** `"all"` leaves the filter off, which asks for drafts and sent alike. */
  status?: PlanStatus | "all";
  /** Debounced here, so a call site can pass the box's value directly. */
  search?: string;
  /** 1-based, the way the endpoint counts. Changing it refetches. */
  page?: number;
  pageSize?: number;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

const NO_PLANS: SupplementPlanSummary[] = [];

export function useSupplementPlans({
  client,
  status = "all",
  search = "",
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseSupplementPlansOptions): SupplementPlansState {
  const getToken = useTokenGetter();

  // Only the search is debounced: the tab, the page and the client are
  // picked, not typed, so making any of them wait would just feel slow.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const isFirstSearch = useRef(true);

  useEffect(() => {
    // The first pass must not wait — the panel would otherwise open on a blank
    // list for no reason, since there is nothing typed to debounce.
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      setDebouncedSearch(search);
      return;
    }

    const timer = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [search]);

  const query = useQuery({
    queryKey: [
      ...SUPPLEMENTS_QUERY_KEY,
      "list",
      client ?? null,
      status,
      debouncedSearch,
      page,
      pageSize,
    ] as const,
    queryFn: ({ signal }) =>
      fetchSupplementPlans(getToken, {
        client,
        status,
        search: debouncedSearch,
        page,
        pageSize,
        signal,
      }),
    // The rows already on screen stay while the next page is fetched — cached
    // across mounts too, so a tab switched away from and back shows its last
    // page instantly while a fresh read runs behind it.
    placeholderData: keepPreviousData,
  });

  const refused = query.error instanceof ApiError && query.error.status === 403;

  return {
    // Back to nothing on a real failure: a row from a page that is no longer
    // confirmed current is worse than none.
    plans: query.isError ? NO_PLANS : query.data?.plans ?? NO_PLANS,
    count: query.isError ? 0 : query.data?.count ?? 0,
    status: query.isError ? "failed" : query.isFetching ? "loading" : "ready",
    error: query.isError && !refused ? failureMessage(query.error) : undefined,
    refused: query.isError && refused,
    reload: () => {
      void query.refetch();
    },
  };
}

export type SupplementPlanStatus = "loading" | "ready" | "missing" | "failed";

export interface SupplementPlanState {
  plan?: SupplementPlanRecord;
  status: SupplementPlanStatus;
  /** Set only when `status` is `failed`. */
  error?: string;
  reload: () => void;
}

/**
 * One plan, whole — what the create screen opens on.
 *
 * `missing` rather than `failed` for a 404, because the two send a reader
 * somewhere different: a plan that was deleted is not a plan sitting behind an
 * error, and telling someone to try again for one would be telling them to
 * wait for something that is not coming.
 */
export function useSupplementPlan(publicId: string): SupplementPlanState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: [...SUPPLEMENTS_QUERY_KEY, "detail", publicId] as const,
    queryFn: ({ signal }) => fetchSupplementPlan(getToken, publicId, signal),
    enabled: !!publicId,
    // A plan that is not there is not there. Retrying a 404 only delays the
    // screen that says so.
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status === 404
        ? false
        : failureCount < 2,
  });

  const missing = query.error instanceof ApiError && query.error.status === 404;

  return {
    plan: query.data,
    status: !publicId
      ? "ready"
      : query.isError
      ? missing
        ? "missing"
        : "failed"
      : query.isPending
      ? "loading"
      : "ready",
    error: query.isError && !missing ? failureMessage(query.error) : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}

export interface SupplementPlanWrites {
  /**
   * Writes a new plan and hands back what the backend made of it. Rejects if
   * the request does, leaving the screen to report it — nothing is shown as
   * written unless the server said so.
   */
  create: (supplements: PlanSupplement[]) => Promise<SupplementPlanRecord>;
  /** Rewrites one, whole. Rejects the same way. */
  save: (
    publicId: string,
    supplements: PlanSupplement[]
  ) => Promise<SupplementPlanRecord>;
  /** Sends it, and hands back the backend's own word on what happened. */
  send: (publicId: string, emails: string[]) => Promise<string | undefined>;
  /**
   * Copies one and hands back the copy, a draft with the same supplements on
   * it. Rejects the same way the others do — nothing is shown as copied unless
   * the server said so.
   */
  duplicate: (publicId: string) => Promise<SupplementPlanRecord>;
}

/**
 * The writes, for the screens that make them.
 *
 * Separate from `useSupplementPlans` because the two have separate callers:
 * the panel lists and the create screen writes, and neither wants the other's
 * query running beside it. What they share is the cache key — every write
 * throws away everything this module has cached, so the list the screen
 * returns to has heard of what was just written.
 *
 * The whole key and not just the list: a plan that was rewritten has a detail
 * cached under it too, and leaving that behind would reopen the plan on the
 * cards it had before the save. The Create plan screen's totals go with it,
 * since one of the three is this kind.
 */
export function useSupplementPlanWrites(clientId: string): SupplementPlanWrites {
  const getToken = useTokenGetter();
  const queryClient = useQueryClient();

  const refresh = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: SUPPLEMENTS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: PLAN_COUNTS_QUERY_KEY }),
      ]).then(() => undefined),
    [queryClient]
  );

  const create = useCallback(
    async (supplements: PlanSupplement[]) => {
      const plan = await createSupplementPlan(getToken, {
        clientId,
        supplements,
      });
      await refresh();
      return plan;
    },
    [getToken, clientId, refresh]
  );

  const save = useCallback(
    async (publicId: string, supplements: PlanSupplement[]) => {
      const saved = await updateSupplementPlan(getToken, publicId, {
        clientId,
        supplements,
      });
      await refresh();
      return saved;
    },
    [getToken, clientId, refresh]
  );

  const send = useCallback(
    async (publicId: string, emails: string[]) => {
      const message = await sendSupplementPlan(getToken, publicId, emails);
      // `status` and `activated_at` both move on the record, which the row
      // reads — so the list is asked again rather than patched.
      await refresh();
      return message;
    },
    [getToken, refresh]
  );

  const duplicate = useCallback(
    async (publicId: string) => {
      const copy = await duplicateSupplementPlan(getToken, publicId);
      // A plan the list has never seen, and one more against the workspace's
      // total — so the cache goes the same way it does after a create.
      await refresh();
      return copy;
    },
    [getToken, refresh]
  );

  return { create, save, send, duplicate };
}
